import * as Model from "@effect/sql/Model";
import { Schema } from "effect";

/**
 * What happened to a credential.
 *
 * - `link_minted`: a collection link was created for the person
 * - `collected`: the person handed the secret over and the row was written
 * - `leased`: a lease was minted for a container acting as them
 * - `lease_denied`: a redemption was refused (invalid / expired / consumed)
 * - `revoked`: the owner withdrew consent
 *
 * Stored as plain varchar, never a pgEnum — a new event kind must not require an
 * `ALTER TYPE` and the M6 two-deploy dance.
 *
 * @since 1.0.0
 */
export const CredentialEventKind = Schema.Literal(
  "link_minted",
  "collected",
  "leased",
  "lease_denied",
  "revoked",
);
export type CredentialEventKind = Schema.Schema.Type<typeof CredentialEventKind>;

/**
 * Credential event model — the audit row a person reads about their OWN
 * credentials (OL-63).
 *
 * Impersonation is only acceptable if the person impersonated can see it, so
 * `personId` is the subject and `listForPerson` is scoped to it. `actorPersonId`
 * records who did the thing when it was not the owner.
 *
 * `detail` is free-form JSON carrying ids, names and reasons — **never secret
 * material and never a prefix of one**. It is deliberately `Schema.Unknown`
 * rather than a struct: an audit row must be able to record something the
 * schema did not anticipate, and nothing reads it as a join key.
 *
 * @since 1.0.0
 */
export class CredentialEvent extends Model.Class<CredentialEvent>("CredentialEvent")({
  id: Model.Generated(Schema.String),

  personId: Schema.String,
  customerId: Schema.String,

  /** The credential concerned. `null` for events that precede one. */
  credentialId: Schema.NullOr(Schema.String),

  kind: CredentialEventKind,

  /** Who acted, when it was not the owner. */
  actorPersonId: Schema.NullOr(Schema.String),

  runId: Schema.NullOr(Schema.String),
  runnerId: Schema.NullOr(Schema.String),
  leaseId: Schema.NullOr(Schema.String),

  /** Ids, names, reasons. NEVER secret material and never a prefix of one. */
  detail: Schema.NullOr(Schema.Unknown),

  createdAt: Model.DateTimeInsertFromDate,
}) {}
